import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { useScreenMatch } from '@common/hooks/screenMatch';
import { useTranslation } from '@common/hooks/translation';

import { useOutsideClick } from '../../hooks/outsideClick';
import MobileDropdownOptions from './mobileOptions';
import DropdownOptions from './options';

import './style.scss';

const getCurrentValues = (multiSelect, value) => {
  if (multiSelect) return Array.isArray(value) ? value : [];
  return (value || value === 0) ? [value] : [];
};

const getSelectedOptions = (options, currentValues) => (
  options.filter((item) => currentValues.includes(item.value))
);

function Dropdown({
  value: _value,
  placeholder,
  options = [],
  className,
  onChange,
  onToggleOpen,
  showClearIcon,
  onClear,
  showSelected = true,
  showArrow = true,
  multiSelect = false,
  adaptive,
  additionalInfo,
  reactNode,
}) {
  const t = useTranslation();
  const [open, setOpen] = useState(false);
  const isMobile = useScreenMatch('mobile');
  const [value, setValue] = useState(_value);

  const hasCancelSaveActions = adaptive && isMobile && multiSelect;

  useEffect(() => {
    setValue(_value);
  }, [_value]);

  const selectRef = useOutsideClick(() => {
    if (adaptive && isMobile) return;

    setOpen(false);
    onToggleOpen?.(false);
  }, [onToggleOpen, adaptive, isMobile], null, adaptive && isMobile && open);

  const toggleOpen = (e) => {
    e?.stopPropagation();
    setOpen(!open);
    onToggleOpen?.(!open);
  };

  const [appliedCurrentValues, currentValues] = useMemo(() => (
    [getCurrentValues(multiSelect, _value), getCurrentValues(multiSelect, value)]
  ), [multiSelect, _value, value]);

  const [appliedSelectedOptions, selectedOptions] = useMemo(
    () => [
      getSelectedOptions(options, appliedCurrentValues),
      getSelectedOptions(options, currentValues),
    ],
    [options, appliedCurrentValues, currentValues],
  );

  const select = (e, option) => {
    e.stopPropagation();

    if (multiSelect) {
      const isSelected = currentValues.includes(option.value);
      let newValues;
      const DocumentTypeEnumAll = 0;

      if (option.value === DocumentTypeEnumAll) {
        if (isSelected) {
          newValues = [];
        } else {
          newValues = options.map(item => item.value);
        }
      }
      else if (isSelected) {
        newValues = currentValues.filter(v => v !== option.value && v !== DocumentTypeEnumAll);
      } else {
        newValues = [...currentValues, option.value];

        const allSelectableValues = options.filter(item => item.value !== DocumentTypeEnumAll).map(item => item.value);
        const individualSelectedCount = newValues.filter(v => v !== DocumentTypeEnumAll).length;

        if (individualSelectedCount === allSelectableValues.length) {
          newValues = options.map(item => item.value);
        }
      }

      if (hasCancelSaveActions) setValue(newValues);
      else onChange?.({ value: newValues });
    } else {
      onChange?.(option);
      setOpen(false);
      onToggleOpen?.(false);
    }
  };

  const clear = (e) => {
    e.stopPropagation();
    onClear?.(multiSelect ? { value: [] } : undefined);
  };

  const getDisplayText = () => {
    if (multiSelect) {
      const actualSelected = appliedSelectedOptions.filter((opt) => opt.value !== 0);

      if (actualSelected.length === 0) {
        return placeholder || '';
      }
      if (actualSelected.length === 1) {
        return actualSelected[0].name;
      }
      return t('{1} selections', actualSelected.length);
    }

    const selected = appliedSelectedOptions.length > 0 ? appliedSelectedOptions[0] : null;
    return (showSelected && selected?.name) || placeholder || '';
  };

  const primarySelected = selectedOptions.length > 0 ? selectedOptions[0] : null;

  const canClear = showClearIcon && selectedOptions.length > 0;

  const Options = adaptive && isMobile ? MobileDropdownOptions : DropdownOptions;

  const onCancel = () => toggleOpen();
  const onSave = () => {
    onChange({ value });
    toggleOpen();
  };

  return (
    <div
      className={`app-dropdown-view-w-t ${open ? 'active' : ''} ${className || ''}`}
      onClick={toggleOpen}
      ref={selectRef}
    >
      <div className="dr-match-wrp">
        {primarySelected?.countryAlias ? (
          <>
            <span
              className={`flag icon-${(primarySelected.flagAlias || primarySelected.countryAlias)?.toLowerCase()} flag`}
            />
            <p className="dr-match-txt">{getDisplayText()}</p>
            {canClear && <i className="dr-icon dr-icon-clear global-icon icon-regular-close" onClick={clear} />}
            {showArrow && <i className={`dr-icon global-icon ${open ? 'icon-arrow-up4' : 'icon-arrow-down5'}`} />}
          </>
        ) : (
          <>
            <p className="dr-match-txt">{getDisplayText()}</p>
            {canClear && <i className="dr-icon dr-icon-clear global-icon icon-regular-close" onClick={clear} />}
            {showArrow && <i className={`dr-icon global-icon ${open ? 'icon-arrow-up4' : 'icon-arrow-down5'}`} />}
          </>
        )}
      </div>

      {open && (
        <Options
          options={options}
          multiSelect={multiSelect}
          values={currentValues}
          onSelect={select}
          onClose={toggleOpen}
          hasCancelSaveActions={hasCancelSaveActions}
          onCancel={onCancel}
          onSave={onSave}
          className={className}
          additionalInfo={additionalInfo}
          reactNode={reactNode}
        />
      )}
    </div>
  );
}

Dropdown.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  ]),
  multiSelect: PropTypes.bool,
  placeholder: PropTypes.node,
  className: PropTypes.string,
  options: PropTypes.array.isRequired,
  onChange: PropTypes.func,
  onToggleOpen: PropTypes.func,
  showSelected: PropTypes.bool,
  showArrow: PropTypes.bool,
  showClearIcon: PropTypes.bool,
  onClear: PropTypes.func,
  adaptive: PropTypes.bool,
  additionalInfo: PropTypes.string,
};

export default Dropdown;
